import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import TickerTimeout from "../../../utils/TickerTimeout";
import { setShowPotCollectionGrandJackpot } from "../../../State/actions";
import { Settings } from "../../../settings";
import { PixiSettings } from "../../../pixiSettings";
import { FONT_STYLES, scaleStyle } from "../../../fontStyles";
import { soundManager } from "../../../SoundManager";
import { SOUND_NAME } from "../../../sounds";
import { SFX_SPRITE_MAP } from "../../../sfxSpriteMap";
import SpineAtlasUploader from "../../../utils/SpineAtlasUploader";
import FXManager from "../../../Engine/Effects/FXManager";

// Total popup duration synced to gjp_win one-shot (~3s per sfxSpriteMap / mp3)
const GJP_WIN_DURATION_MS = SFX_SPRITE_MAP.gjp_win?.[1] || 3000;

// Device and orientation-specific config (same as PotCollectionOutroDialogController)
// Y offsets applied to child text (not container) because Spine overwrites container transforms.
const DEVICE_MULTIPLIERS = {
  MOBILE_SMALL_PORTRAIT: {
    scale: 1.0,
    offset: 0,
    buttonYOffset: 1.25,
    congratsYOffset: 0,
    youWonYOffset: 0,
  },
  MOBILE_PORTRAIT: {
    scale: 1.0,
    offset: 0,
    buttonYOffset: 2.75,
    congratsYOffset: 0,
    youWonYOffset: 0,
  },
  MOBILE_LANDSCAPE: {
    scale: 0.8,
    offset: 0,
    buttonYOffset: 1.0,
    congratsYOffset: 0,
    youWonYOffset: 0,
  },
  TABLET_PORTRAIT: {
    scale: 1.4,
    offset: 0,
    buttonYOffset: 0,
    congratsYOffset: 0,
    youWonYOffset: 0,
  },
  TABLET_LANDSCAPE: {
    scale: 1.3,
    offset: 0,
    buttonYOffset: 0,
    congratsYOffset: 0,
    youWonYOffset: 0,
  },
  // Desktop: grand_jackpot skin is tall — shrink so the top congrats/you-won text
  // fits the short landscape viewport, then push the button back DOWN (positive
  // buttonYOffset) so TAP TO CONTINUE realigns with the other popups. Tune both.
  DESKTOP: {
    scale: 0.9,
    offset: 0,
    buttonYOffset: 0.3,
    congratsYOffset: 0,
    youWonYOffset: 0,
    // Desktop shrinks the spine to 0.9 so the tall grand_jackpot skin fits, which
    // also shrinks the text. This negates that on the text only (1/scale) so
    // TAP TO CONTINUE / X BET render the same size as the other popups.
    compensateTextScale: true,
  },
};

/**
 * PotCollectionGrandJackpotDialogController
 *
 * Manages the Grand Jackpot congratulations popup (GDD §14.1–14.2).
 * Uses the win_celebration spine with the "grand_jackpot" skin.
 *
 * Text slots:
 *   - txt_slot_congratulations  → "CONGRATULATIONS!" (localized)
 *   - txt_slot_you_won          → "YOU WON THE GRAND JACKPOT!" (localized)
 *   - txt_slot_value            → formatted 5,000x value
 *   - txt_slot_multiplier       → "5,000 X BET"
 *   - txt_slot_tap_to_continue  → "TAP TO CONTINUE"
 *
 * Flow: setSkin("grand_jackpot") → in → idle → user click OR auto-close → out → dispatch hide
 *       Total auto-close duration matches gjp_win sound (~3s: 1s in + 1s idle + 1s out).
 *       Does NOT trigger scene transition — bonus continues after popup dismissal.
 */
class PotCollectionGrandJackpotDialogController {
  constructor(
    container,
    dimensionsContext,
    dispatch,
    symbolHeight,
    overlayRef,
    t,
  ) {
    this.container = container;
    this.dimensionsContext = dimensionsContext;
    this.dispatch = dispatch;
    this.symbolHeight = symbolHeight;
    this.overlayRef = overlayRef;
    this.t = t;

    this.dimensions = dimensionsContext.dimensions;

    // Spine instance
    this.gjpSpine = null;
    this.isPlaying = false;
    this.initialized = false;
    this.isWaitingForClick = false;

    // User interaction state
    this.canSkipAnimation = false;
    this.isHandlingUserClick = false;
    this.hasHandledCloseClick = false;
    this.debounceTimeout = null;
    this.skipProtectionTimeout = null;
    this.inIdleTimeout = null;
    this.autoCloseTimeout = null;

    // Win data
    this._gjpValue = 0;
    this._betAmount = 0;

    // Resolve callback for awaiting dismissal
    this._dismissResolve = null;

    // Counter (attached to spine slot)
    this.counterContainer = null;
    this.counterText = null;
    this._counterBaseStyle = null;

    // Multiplier (attached to spine slot)
    this.multiplierContainer = null;
    this.multiplierText = null;

    // Congratulations text (attached to spine slot)
    this.congratsContainer = null;
    this.congratsText = null;

    // "You won" text (attached to spine slot)
    this.youWonContainer = null;
    this.youWonText = null;

    // Button (attached to spine slot)
    this.buttonContainer = null;
    this.buttonText = null;

    // Click handler (set by component via setButtonClickHandler)
    this._buttonClickHandler = null;

    // Internal caches
    this._deviceMultipliersCache = null;
    this._availableAnimationsCache = null;
    this._lastDevice = null;
    this._lastOrientation = null;

    this.initializeSpine();
  }

  // ===================================================================
  //  CONFIG / SCALING
  // ===================================================================

  setButtonClickHandler(handler) {
    this._buttonClickHandler = handler;
  }

  getDeviceMultipliers() {
    const { device, orientation, isMobileSmall } = this.dimensionsContext;

    if (
      this._deviceMultipliersCache !== null &&
      this._lastDevice === device &&
      this._lastOrientation === orientation &&
      this._lastIsMobileSmall === isMobileSmall
    ) {
      return this._deviceMultipliersCache;
    }

    let multipliers;
    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        multipliers = isMobileSmall
          ? DEVICE_MULTIPLIERS.MOBILE_SMALL_PORTRAIT
          : DEVICE_MULTIPLIERS.MOBILE_PORTRAIT;
      } else {
        multipliers = DEVICE_MULTIPLIERS.MOBILE_LANDSCAPE;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        multipliers = DEVICE_MULTIPLIERS.TABLET_PORTRAIT;
      } else {
        multipliers = DEVICE_MULTIPLIERS.TABLET_LANDSCAPE;
      }
    } else {
      multipliers = DEVICE_MULTIPLIERS.DESKTOP;
    }

    this._deviceMultipliersCache = multipliers;
    this._lastDevice = device;
    this._lastOrientation = orientation;
    this._lastIsMobileSmall = isMobileSmall;
    return multipliers;
  }

  applyScaleAndCenter() {
    if (!this.gjpSpine || !this.dimensions) return;

    const nativeSize = Settings.SYMBOL_HEIGHT;
    const targetSize = this.symbolHeight || nativeSize;
    let scale = Math.max(0.1, Math.min(targetSize / nativeSize, 2));

    const { scale: deviceScaleMultiplier, offset: deviceOffsetMultiplier } =
      this.getDeviceMultipliers();
    scale *= deviceScaleMultiplier;

    this.gjpSpine.scale.set(scale);

    const baseOffset = this.symbolHeight || 0;
    this.gjpSpine.x = 0;
    this.gjpSpine.y = -baseOffset * deviceOffsetMultiplier;

    // Keep coin shower proportional to spine on resize
    if (this._coinShowerContainer && !this._coinShowerContainer.destroyed) {
      this._coinShowerContainer.scale.set(scale * 2.5);
    }
  }

  updateDimensions(dimensionsContext, symbolHeight) {
    const oldDimensions = this.dimensionsContext?.dimensions;
    const newDimensions = dimensionsContext?.dimensions;

    const dimensionsChanged =
      !oldDimensions ||
      oldDimensions.width !== newDimensions?.width ||
      oldDimensions.height !== newDimensions?.height ||
      this.dimensionsContext?.device !== dimensionsContext?.device ||
      this.dimensionsContext?.orientation !== dimensionsContext?.orientation;

    const symbolHeightChanged = this.symbolHeight !== symbolHeight;

    if (!dimensionsChanged && !symbolHeightChanged) return;

    this.dimensionsContext = dimensionsContext;
    this.dimensions = dimensionsContext.dimensions;
    this.symbolHeight = symbolHeight;

    if (
      this._lastDevice !== dimensionsContext.device ||
      this._lastOrientation !== dimensionsContext.orientation ||
      this._lastIsMobileSmall !== dimensionsContext.isMobileSmall
    ) {
      this._deviceMultipliersCache = null;
      this._lastDevice = dimensionsContext.device;
      this._lastOrientation = dimensionsContext.orientation;
    }

    if (
      this.gjpSpine &&
      this.dimensions &&
      (dimensionsChanged || symbolHeightChanged)
    ) {
      if (this.symbolHeight && this.symbolHeight > 0) {
        this.applyScaleAndCenter();
        this.applyCongratulationsOffset();
        this.applyYouWonOffset();
        this.applyButtonOffset();
        this.applyTextScaleCompensation();
      }
    }
  }

  // ===================================================================
  //  SPINE CREATION
  // ===================================================================

  initializeSpine() {
    try {
      this.gjpSpine = this.createSpine();

      if (!this.gjpSpine) return;
      if (!this.gjpSpine.skeleton || !this.gjpSpine.state) return;
      if (!this.gjpSpine.skeleton.data) return;

      this.container.addChild(this.gjpSpine);
      this.gjpSpine.alpha = 0.001;
      this.gjpSpine.zIndex = 20;

      this.applyScaleAndCenter();

      if (this.gjpSpine.parent !== this.container) return;

      this.initialized = true;
      this._availableAnimationsCache = null;

      // Configure animation mixing for smooth transitions
      const stateData = this.gjpSpine.state.data;
      stateData.setMix("in", "idle", 0.15);
      stateData.setMix("idle", "out", 0.25);

      this.warmUpSpine();
      this.initializeCongratulations();
      this.initializeYouWon();
      this.initializeCounter();
      this.initializeMultiplier();
      this.initializeButton();

      // Force-upload atlas textures to GPU
      SpineAtlasUploader.upload("win_celebration");
    } catch {
      // Silent fail
    }
  }

  createSpine() {
    try {
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "win_celebration-skeleton";
      const atlasAlias = "win_celebration-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) return null;

      const spine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (!spine?.skeleton?.data?.animations) return null;

      return spine;
    } catch {
      return null;
    }
  }

  warmUpSpine() {
    if (!this.gjpSpine?.state) return;
    try {
      this.applySkin();

      const animations = this.getAvailableAnimations();
      if (animations.length > 0) {
        this.gjpSpine.state.setAnimation(0, animations[0], false);
        this.gjpSpine.state.update(0.016);
        this.gjpSpine.skeleton.updateWorldTransform();
      }
      this.gjpSpine.alpha = 0.001;
    } catch {
      // Silent fail
    }
  }

  getAvailableAnimations() {
    if (!this.gjpSpine?.skeleton?.data?.animations) return [];
    if (this._availableAnimationsCache !== null)
      return this._availableAnimationsCache;

    const animations = this.gjpSpine.skeleton.data.animations.map(
      (a) => a.name,
    );
    this._availableAnimationsCache = animations;
    return animations;
  }

  /**
   * Apply the "grand_jackpot" skin
   */
  applySkin() {
    if (!this.gjpSpine?.skeleton) return;

    try {
      const skinData = this.gjpSpine.skeleton.data.findSkin("grand_jackpot");
      if (skinData) {
        this.gjpSpine.skeleton.setSkin(skinData);
        this.gjpSpine.skeleton.setSlotsToSetupPose();
      }
    } catch {
      // Silent fail — skin may not exist
    }
  }

  /**
   * Max text width in spine-local coords for font-size fitting.
   * Uses screen width only — bone width is too narrow and over-shrinks text.
   */
  _getScreenLocalMaxWidth(screenRatio = 0.85) {
    const spineScale = Math.abs(this.gjpSpine?.scale?.x || 1);
    return spineScale > 0
      ? ((this.dimensions?.width || 800) * screenRatio) / spineScale
      : (this.dimensions?.width || 800) * screenRatio;
  }

  /**
   * Reduce font size so text fits within screen width (spine-local coords).
   */
  _fitFontSizeToWidth(text, baseStyle, baseFontSize, screenRatio = 0.85) {
    let fontSize = baseFontSize;
    const maxPixelWidth = this._getScreenLocalMaxWidth(screenRatio);
    const tempStyle = new PIXI.TextStyle(scaleStyle(baseStyle, fontSize));
    const metrics = PIXI.TextMetrics.measureText(text, tempStyle);
    if (metrics.width > maxPixelWidth && maxPixelWidth > 0) {
      fontSize = Math.max(
        10,
        Math.floor(fontSize * (maxPixelWidth / metrics.width)),
      );
    }
    return fontSize;
  }

  // ===================================================================
  //  CONGRATULATIONS TEXT
  // ===================================================================

  initializeCongratulations() {
    if (!this.gjpSpine || !this.gjpSpine.skeleton) return;

    const slot = this.gjpSpine.skeleton.findSlot("txt_slot_congratulations");
    if (!slot) return;

    this.congratsContainer = new PIXI.Container();
    this.congratsContainer.name = "pc_gjp_congrats_attached_to_slot";

    const style = FONT_STYLES.introTapToStart;
    const baseFontSize = 72;
    const congratsLabel = `${this.t("popup_grand_jackpot_title").toUpperCase()}!`;
    const fontSize = this._fitFontSizeToWidth(
      congratsLabel,
      style,
      baseFontSize,
    );

    this.congratsText = new PIXI.Text(
      congratsLabel,
      new PIXI.TextStyle(scaleStyle(style, fontSize)),
    );
    this.congratsText.anchor.set(0.5);

    this.congratsContainer.addChild(this.congratsText);
    this.congratsContainer.scale.set(1);

    this.gjpSpine.addSlotObject(
      "txt_slot_congratulations",
      this.congratsContainer,
    );
  }

  applyCongratulationsOffset() {
    if (!this.congratsText) return;
    const config = this.getDeviceMultipliers();
    this.congratsText.y =
      (config.congratsYOffset || 0) * (this.symbolHeight || 0);
  }

  // ===================================================================
  //  "YOU WON" TEXT
  // ===================================================================

  initializeYouWon() {
    if (!this.gjpSpine || !this.gjpSpine.skeleton) return;

    const slot = this.gjpSpine.skeleton.findSlot("txt_slot_you_won");
    if (!slot) return;

    this.youWonContainer = new PIXI.Container();
    this.youWonContainer.name = "pc_gjp_you_won_attached_to_slot";

    const style = FONT_STYLES.introTapToStart;
    const baseFontSize = 72;
    const youWonLabel = this.t("popup_grand_jackpot_label").toUpperCase();
    const fontSize = this._fitFontSizeToWidth(youWonLabel, style, baseFontSize);

    this.youWonText = new PIXI.Text(
      youWonLabel,
      new PIXI.TextStyle(scaleStyle(style, fontSize)),
    );
    this.youWonText.anchor.set(0.5);

    this.youWonContainer.addChild(this.youWonText);
    this.youWonContainer.scale.set(1);

    this.gjpSpine.addSlotObject("txt_slot_you_won", this.youWonContainer);
  }

  applyYouWonOffset() {
    if (!this.youWonText) return;
    const config = this.getDeviceMultipliers();
    this.youWonText.y = (config.youWonYOffset || 0) * (this.symbolHeight || 0);
  }

  // ===================================================================
  //  VALUE (txt_slot_value) — intentionally empty for GJP
  // ===================================================================

  initializeCounter() {
    // No-op: GJP shows "5.000 X BET" in the multiplier slot instead
  }

  updateCounterDisplay() {
    // No-op
  }

  // ===================================================================
  //  MULTIPLIER — "5.000 X BET" (single text in lower slot)
  // ===================================================================

  initializeMultiplier() {
    if (!this.gjpSpine || !this.gjpSpine.skeleton) return;

    const multiplierSlot = this.gjpSpine.skeleton.findSlot(
      "txt_slot_multiplier",
    );
    if (!multiplierSlot) return;

    this.multiplierContainer = new PIXI.Container();
    this.multiplierContainer.name = "pc_gjp_multiplier_attached_to_slot";

    const counterBaseStyle = FONT_STYLES.winCelebrationCounter;
    this._counterBaseStyle = counterBaseStyle;
    this._multiplierBaseFontSize = counterBaseStyle.fontSize;

    this.multiplierText = new PIXI.Text(
      "",
      new PIXI.TextStyle(
        scaleStyle(counterBaseStyle, this._multiplierBaseFontSize),
      ),
    );
    this.multiplierText.anchor.set(0.5);

    this.multiplierContainer.addChild(this.multiplierText);

    const bone = multiplierSlot.bone;
    this._multiplierBoneWidth = bone ? bone.data.length || 400 : 400;

    this.gjpSpine.addSlotObject(
      "txt_slot_multiplier",
      this.multiplierContainer,
    );
  }

  updateMultiplierDisplay(multiplier) {
    if (!this.multiplierText) return;

    const text = `${multiplier.toLocaleString("en-US")} X BET`;
    const fontSize = this._fitFontSizeToWidth(
      text,
      this._counterBaseStyle,
      this._multiplierBaseFontSize,
    );

    this.multiplierText.text = text;
    this.multiplierText.style = new PIXI.TextStyle(
      scaleStyle(this._counterBaseStyle, fontSize),
    );
    this.multiplierText.scale.set(1);
    if (this.multiplierContainer) {
      this.multiplierContainer.scale.set(1);
    }
  }

  // ===================================================================
  //  BUTTON
  // ===================================================================

  initializeButton() {
    if (!this.gjpSpine || !this.gjpSpine.skeleton) return;

    const tapSlot = this.gjpSpine.skeleton.findSlot("txt_slot_tap_to_continue");
    if (!tapSlot) return;

    this.buttonContainer = new PIXI.Container();
    this.buttonContainer.name = "pc_gjp_continue_attached_to_slot";

    const buttonLabel = this.t("tap_to_continue").toUpperCase();

    const style = FONT_STYLES.introTapToStart;
    const btnFontSize = 42;

    this.buttonText = new PIXI.Text(
      buttonLabel,
      new PIXI.TextStyle(scaleStyle(style, btnFontSize)),
    );
    this.buttonText.anchor.set(0.5);

    this.buttonContainer.addChild(this.buttonText);

    const bone = tapSlot.bone;
    this._tapBoneWidth = bone ? bone.data.length || 400 : 400;
    if (this.buttonText.width > this._tapBoneWidth && this._tapBoneWidth > 0) {
      const scale = this._tapBoneWidth / this.buttonText.width;
      this.buttonContainer.scale.set(scale);
    }

    this.gjpSpine.addSlotObject(
      "txt_slot_tap_to_continue",
      this.buttonContainer,
    );

    // Make the button clickable — triggers fullscreen + dismissal via component handler
    this.buttonContainer.eventMode = "static";
    this.buttonContainer.cursor = "pointer";
    this.buttonContainer.hitArea = new PIXI.Rectangle(
      -this._tapBoneWidth / 2, -40, this._tapBoneWidth, 80
    );
    this.buttonContainer.on("pointerdown", (evt) => {
      if (evt?.stopPropagation) evt.stopPropagation();
      if (this._buttonClickHandler) {
        this._buttonClickHandler(evt);
      }
    });

    this.applyButtonOffset();
  }

  applyButtonOffset() {
    // Spine's updateSlotObject overwrites the container's localTransform every frame,
    // so we offset the TEXT inside the container instead of the container itself.
    if (!this.buttonText) {

      return;
    }
    const config = this.getDeviceMultipliers();
    const yOffset = (config.buttonYOffset || 0) * (this.symbolHeight || 0);
    this.buttonText.y = yOffset;
  }

  // Negate the tier's spine shrink on the TEXT so it stays the same size as the
  // other popups. The text slots are children of bone-attached containers, so
  // they inherit the spine scale; we counter-scale the text CHILD (the container
  // transform is overwritten by Spine every frame — same reason as the Y offset).
  // Gated by compensateTextScale so only tiers that diverge from the shared
  // baseline (currently desktop @ 0.9) are affected; others stay 1:1.
  applyTextScaleCompensation() {
    const { scale, compensateTextScale } = this.getDeviceMultipliers();
    const comp = compensateTextScale && scale > 0 ? 1 / scale : 1;
    if (this.congratsText) this.congratsText.scale.set(comp);
    if (this.youWonText) this.youWonText.scale.set(comp);
    if (this.multiplierText) this.multiplierText.scale.set(comp);
    if (this.buttonText) this.buttonText.scale.set(comp);
  }

  // ===================================================================
  //  ANIMATION HELPERS
  // ===================================================================

  getSpineAnimationDuration(animationName) {
    if (!this.gjpSpine?.skeleton?.data?.animations) return 1000;
    try {
      const animation = this.gjpSpine.skeleton.data.animations.find(
        (anim) => anim.name === animationName,
      );
      if (animation && animation.duration) {
        return Math.round(animation.duration * 1000);
      }
      return 1000;
    } catch {
      return 1000;
    }
  }

  clearAllTimeouts() {
    if (this.skipProtectionTimeout) {
      this.skipProtectionTimeout.clear();
      this.skipProtectionTimeout = null;
    }
    if (this.autoCloseTimeout) {
      this.autoCloseTimeout.clear();
      this.autoCloseTimeout = null;
    }
    if (this.debounceTimeout) {
      this.debounceTimeout.clear();
      this.debounceTimeout = null;
    }
    if (this.inIdleTimeout) {
      this.inIdleTimeout.clear();
      this.inIdleTimeout = null;
    }
  }

  // ===================================================================
  //  AUTO-CLOSE — idle hold sized so in + idle + out ≈ gjp_win duration
  // ===================================================================

  startAutoCloseTimer() {
    if (this.autoCloseTimeout) {
      this.autoCloseTimeout.clear();
    }

    const inMs = this.getSpineAnimationDuration("in");
    const outMs = this.getSpineAnimationDuration("out");
    const idleDelay = Math.max(0, GJP_WIN_DURATION_MS - inMs - outMs);

    this.autoCloseTimeout = new TickerTimeout(() => {
      if (this.isPlaying && !this.hasHandledCloseClick) {
        this.hasHandledCloseClick = true;
        this.playClosingAnimation();
      }
    }, idleDelay);
  }

  // ===================================================================
  //  ANIMATION SEQUENCE
  // ===================================================================

  /**
   * Set the Grand Jackpot multiplier for display. Called before startSequence.
   * @param {number} gjpMultiplier - The Grand Jackpot multiplier (e.g. 5000 from state)
   */
  setGrandJackpotMultiplier(gjpMultiplier) {
    this._gjpMultiplier = gjpMultiplier || 5000;
    this.updateMultiplierDisplay(this._gjpMultiplier);
  }

  /**
   * Start the GJP popup sequence.
   * Returns a promise that resolves when the user dismisses the popup.
   */
  startSequence() {
    return new Promise((resolve) => {
      if (this.isPlaying || !this.gjpSpine?.state) {
        resolve();
        return;
      }

      this._dismissResolve = resolve;
      this.isPlaying = true;
      this.isWaitingForClick = false;
      this.hasHandledCloseClick = false;
      this.overlayRef.current?.fadeInBackground();

      this.isHandlingUserClick = false;
      this.canSkipAnimation = false;

      this.clearAllTimeouts();

      // Fade out active feature music (background or ambient — they're exclusive)
      if (soundManager.isAmbientActive) {
        soundManager.fadeOut(SOUND_NAME.feature_ambient_music, 500);
      } else {
        soundManager.fadeOut(SOUND_NAME.feature_background_music, 500);
      }
      // Remember which was active so we can restore it
      this._wasAmbientActive = soundManager.isAmbientActive;

      // Play GJP sounds
      soundManager.play(SOUND_NAME.gjp_win);
      this._startIdleSoundLoop();

      if (
        this.gjpSpine &&
        this.symbolHeight &&
        this.symbolHeight > 0 &&
        this.dimensions
      ) {
        this.applyScaleAndCenter();
      }

      // Clear state
      this.gjpSpine.state.clearTracks();
      this.gjpSpine.state.clearListeners();
      this.gjpSpine.state.apply(this.gjpSpine.skeleton);
      this.gjpSpine.skeleton.setToSetupPose();

      // Apply skin
      this.applySkin();

      // Re-apply text offsets AFTER setToSetupPose (Spine overwrites container transforms)
      this.applyCongratulationsOffset();
      this.applyYouWonOffset();
      this.applyButtonOffset();
      this.applyTextScaleCompensation();

      // SKIP PROTECTION: Block skipping for the first second
      this.skipProtectionTimeout = new TickerTimeout(() => {
        if (this.isPlaying) {
          this.canSkipAnimation = true;
          this.isWaitingForClick = true;
          this.startAutoCloseTimer();
        }
      }, 1000);

      // Play "in" animation while STILL HIDDEN
      this.gjpSpine.state.setAnimation(0, "in", false);

      // Show on NEXT FRAME
      if (this._showRAF) cancelAnimationFrame(this._showRAF);
      this._showRAF = requestAnimationFrame(() => {
        this._showRAF = null;
        if (this.isPlaying && this.gjpSpine) {
          this.gjpSpine.alpha = 1;

          // Start coin shower — between overlay (zIndex 0) and spine (zIndex 20)
          this._startCoinShower();
        }
      });

      const inDuration = this.getSpineAnimationDuration("in");

      this.inIdleTimeout = new TickerTimeout(() => {
        if (this.isPlaying) {
          this.gjpSpine.state.setAnimation(0, "idle", true);
        }
      }, inDuration);
    });
  }

  // ===================================================================
  //  USER INTERACTION
  // ===================================================================

  handleUserClick() {
    if (!this.isPlaying || !this.gjpSpine?.state) return false;
    if (this.debounceTimeout) return false;
    if (!this.canSkipAnimation) return false;
    if (!this.isWaitingForClick) return false;

    this.debounceTimeout = new TickerTimeout(() => {
      this.debounceTimeout = null;
    }, 300);

    if (this.autoCloseTimeout) {
      this.autoCloseTimeout.clear();
      this.autoCloseTimeout = null;
    }

    this.hasHandledCloseClick = true;
    this.playClosingAnimation();
    return true;
  }

  playClosingAnimation() {
    if (!this.isWaitingForClick || !this.gjpSpine?.state) return;

    this.isWaitingForClick = false;

    this.gjpSpine.state.clearListeners();

    // Play "out" animation and its one-shot sound
    this.gjpSpine.state.setAnimation(0, "out", false);

    // Crossfade: fade out idle phase sound so it blends with the out sound
    this._stopIdleSoundLoop();

    this.gjpSpine.state.addListener({
      complete: (entry) => {
        if (entry.animation.name === "out" && this.isPlaying) {
          // Restore whichever feature music was active before the dialog
          if (this._wasAmbientActive) {
            soundManager.fadeIn(SOUND_NAME.feature_ambient_music, 500);
          } else {
            soundManager.fadeIn(SOUND_NAME.feature_background_music, 500);
          }
          this.finishAnimation();
        }
      },
    });
  }

  finishAnimation() {
    if (!this.gjpSpine || !this.initialized) return;

    this.isPlaying = false;
    this.isWaitingForClick = false;
    this.gjpSpine.alpha = 0.001;

    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }

    // Stop sounds
    this._stopIdleSoundLoop();

    this.clearAllTimeouts();

    this.isHandlingUserClick = false;
    this.canSkipAnimation = false;

    // Close the dialog state
    this.dispatch(setShowPotCollectionGrandJackpot(false));

    // Fade out the overlay
    this.overlayRef.current?.fadeOutBackground();

    // Stop coin shower
    this._stopCoinShower();

    // Resolve the awaiting promise so the SpinEngine can continue
    if (this._dismissResolve) {
      this._dismissResolve();
      this._dismissResolve = null;
    }
  }

  // ===================================================================
  //  COIN SHOWER EFFECT
  // ===================================================================

  _startCoinShower() {
    if (!this.container || !FXManager.isReady) {

      return;
    }

    // Create a container for coin shower at zIndex between overlay (0) and spine (20)
    this._coinShowerContainer = new PIXI.Container();
    this._coinShowerContainer.name = "gjp_coin_shower";
    this._coinShowerContainer.zIndex = 10;
    this._coinShowerContainer.y = 0;

    // Scale coin shower to 2× the spine's current scale — always proportional
    const spineScale = this.gjpSpine?.scale.x || 1;
    this._coinShowerContainer.scale.set(spineScale * 2.5);

    this.container.addChild(this._coinShowerContainer);

    FXManager.startCoinShower(this._coinShowerContainer);
  }

  _stopCoinShower() {
    FXManager.stopCoinShower();

    if (this._coinShowerContainer) {
      if (this._coinShowerContainer.parent) {
        this._coinShowerContainer.parent.removeChild(this._coinShowerContainer);
      }
      if (!this._coinShowerContainer.destroyed) {
        this._coinShowerContainer.destroy({ children: true });
      }
      this._coinShowerContainer = null;
    }
  }

  // ===================================================================
  //  IDLE SOUND LOOP
  // ===================================================================

  _startIdleSoundLoop() {
    this._idleSoundActive = true;
    this._playNextIdleSound();
  }

  _playNextIdleSound() {
    if (!this._idleSoundActive) return;
    soundManager.play(SOUND_NAME.generic_popup_idle);
    const duration = SFX_SPRITE_MAP.generic_popup_idle?.[1] || 2750;
    this._idleSoundTimeout = new TickerTimeout(() => {
      this._playNextIdleSound();
    }, duration);
  }

  _stopIdleSoundLoop() {
    this._idleSoundActive = false;
    if (this._idleSoundTimeout) {
      this._idleSoundTimeout.clear();
      this._idleSoundTimeout = null;
    }
    soundManager.stop(SOUND_NAME.generic_popup_idle);
  }

  // ===================================================================
  //  CLEANUP
  // ===================================================================

  cleanup() {
    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }

    this._stopIdleSoundLoop();

    // Stop coin shower
    this._stopCoinShower();

    this.clearAllTimeouts();

    if (this.gjpSpine?.removeSlotObjects) {
      this.gjpSpine.removeSlotObjects();
    }

    if (this.congratsContainer) {
      this.congratsContainer.destroy({ children: true });
      this.congratsContainer = null;
      this.congratsText = null;
    }

    if (this.youWonContainer) {
      this.youWonContainer.destroy({ children: true });
      this.youWonContainer = null;
      this.youWonText = null;
    }

    if (this.counterContainer) {
      this.counterContainer.destroy({ children: true });
      this.counterContainer = null;
      this.counterText = null;
    }

    if (this.multiplierContainer) {
      this.multiplierContainer.destroy({ children: true });
      this.multiplierContainer = null;
      this.multiplierText = null;
    }

    if (this.buttonContainer) {
      this.buttonContainer.destroy({ children: true });
      this.buttonContainer = null;
      this.buttonText = null;
    }

    if (this.gjpSpine) {
      this.gjpSpine.state?.clearListeners();
      this.gjpSpine.state?.clearTracks();
      if (this.gjpSpine.state) this.gjpSpine.state.timeScale = 1.0;

      if (this.container?.children.includes(this.gjpSpine)) {
        this.container.removeChild(this.gjpSpine);
      }
      this.gjpSpine.destroy();
      this.gjpSpine = null;
    }

    this.container = null;
    this.initialized = false;

    if (this._dismissResolve) {
      this._dismissResolve();
      this._dismissResolve = null;
    }
  }
}

export default PotCollectionGrandJackpotDialogController;
